// Impersonation (legacy UserCardComponent + UserImpersonateComponent):
//   GET impersonate/{username}/url → signed URL encoded as a QR code on the user card
//   POST <signed url> → start impersonating · POST impersonate/stop → stop
import { useEffect, useRef, useState } from 'react';
import { BrowserQRCodeSvgWriter } from '@zxing/browser';
import { Alert, Avatar, Box, Button, Stack, TextField, Typography } from '@mui/material';

import { adminApi, apiErrorMessage } from '../../api/client';
import { LoadingBlock } from '../../components/StateBlock';
import { t } from '../../i18n';

type CardUser = { username?: string; name?: string; phone?: string; avatar?: string | null; active?: unknown; can_impersonate?: unknown };

export function UserCardDialog({ user, onClose }: { user: CardUser; onClose: () => void }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const qrRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!user.username || !user.active || !user.can_impersonate) {
      setError(t('parties.cannotImpersonate'));
      return;
    }
    setLoading(true);
    adminApi
      .get(`impersonate/${encodeURIComponent(user.username)}/url`)
      .then((response) => {
        if (response.data?.success && response.data.url) setUrl(String(response.data.url));
        else setError(response.data?.message ?? t('parties.cannotImpersonate'));
      })
      .catch((err) => setError(apiErrorMessage(err, t('parties.cannotImpersonate'))))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    const container = qrRef.current;
    if (!url || !container) return;
    container.innerHTML = '';
    try {
      const svg = new BrowserQRCodeSvgWriter().write(url, 180, 180);
      container.appendChild(svg);
    } catch {
      setError(t('common.actionFailed'));
    }
  }, [url]);

  return (
    <Stack spacing={2} alignItems="center" sx={{ minWidth: 260 }}>
      {user.avatar ? <Avatar src={user.avatar} sx={{ width: 96, height: 96 }} /> : null}
      <Typography variant="h6">{user.name}</Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        {user.username}
        <br />
        {user.phone}
      </Typography>
      {loading ? <LoadingBlock /> : null}
      {error ? <Alert severity="warning">{error}</Alert> : null}
      <Box ref={qrRef} sx={{ width: 180, minHeight: url ? 180 : 0 }} />
      <Stack direction="row" spacing={1} className="no-print">
        <Button variant="contained" disabled={!url} onClick={() => window.print()}>
          {t('parties.print')}
        </Button>
        <Button onClick={onClose}>{t('action.close')}</Button>
      </Stack>
    </Stack>
  );
}

export function ImpersonateDialog({ onClose, onDone }: { onClose: () => void; onDone: (message: string) => void }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    const signed = value.trim();
    if (!signed || signed.length <= 40 || !signed.includes(window.location.host.split(':')[0])) {
      setError(t('parties.invalidImpersonationUrl'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // The signed URL points at …/app/impersonate/{username}?expires=…&signature=…;
      // post it relative to the admin API so the session cookie travels with it.
      const marker = signed.indexOf('/app/impersonate/');
      const target = marker >= 0 ? signed.slice(marker + '/app/'.length) : signed;
      const response = await adminApi.post(target, {});
      if (!response.data?.success) throw new Error(t('parties.cannotImpersonate'));
      onDone(t('parties.impersonatingAs', { name: String(response.data.user?.name ?? '') }));
      window.location.reload();
    } catch (err) {
      setError(apiErrorMessage(err, t('parties.cannotImpersonate')));
    } finally {
      setBusy(false);
    }
  };

  const stop = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await adminApi.post('impersonate/stop', {});
      if (!response.data?.success) throw new Error(t('common.actionFailed'));
      onDone(t('parties.stoppedImpersonating'));
      window.location.reload();
    } catch (err) {
      setError(apiErrorMessage(err, t('common.actionFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ minWidth: { sm: 420 } }}>
      <Typography variant="body2" color="text.secondary">
        {t('parties.impersonateHelp')}
      </Typography>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <TextField autoFocus label={t('parties.scanQrCode')} value={value} onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void start()} fullWidth />
      <Stack direction="row" spacing={1} justifyContent="space-between">
        <Button color="warning" disabled={busy} onClick={() => void stop()}>
          {t('parties.stopImpersonating')}
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>{t('action.close')}</Button>
          <Button variant="contained" disabled={busy || !value} onClick={() => void start()}>
            {t('parties.impersonate')}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
